/**
 * The two state banners at the top of the concept sidebar —
 * ROUTE_INVENTORY §2.10 #37 and #38.
 *
 * Extracted from `ConceptDetailSidebar`, and in one file because they are one
 * decision seen twice: a concept can be in an unusual state, and the banner
 * explains the state and offers the single action that resolves it.
 *
 * They are not variants of each other, though, and the difference is the point:
 *
 * - **Orphaned** is something that happened TO the concept — its source lesson
 *   was deleted. The action is destructive and irreversible, so it is styled
 *   red and goes through a danger confirm.
 * - **Paused** is something the user DID. The action undoes it, so it is a
 *   plain outline button with no confirm at all — there is nothing to warn
 *   about when the whole point is that the state is reversible.
 *
 * Both say the review history is kept, because that is the thing a user is
 * actually worried about in either state.
 */

import {
  AlertTriangle,
  Loader2,
  PauseCircle,
  PlayCircle,
  Trash2,
} from 'lucide-react';
import { Button } from '@ai-tutor/ui';

/**
 * §2.10 #37 — the source lesson is gone.
 *
 * `pending` disables the button and swaps the icon for a spinner. Worth
 * wiring rather than leaving to the mutation's own latency: deleting a concept
 * invalidates every mindspace query, so the gap between click and the sidebar
 * closing is long enough to double-click through.
 */
export function OrphanBanner({
  onRemove,
  pending,
}: {
  onRemove: () => void;
  pending: boolean;
}) {
  return (
    <div className='space-y-2.5 rounded-lg border border-border bg-muted/50 p-3'>
      <div className='flex items-start gap-2'>
        <AlertTriangle
          className='mt-0.5 h-4 w-4 flex-shrink-0 text-warning-emphasis'
          aria-hidden
        />
        <div>
          <p className='text-sm font-medium text-foreground'>
            Source lesson deleted
          </p>
          <p className='mt-1 text-xs leading-relaxed text-muted-foreground'>
            Review history kept – remove if unneeded.
          </p>
        </div>
      </div>
      <Button
        variant='outline'
        size='sm'
        onClick={onRemove}
        disabled={pending}
        className='w-full gap-1.5 border-border text-destructive-emphasis hover:bg-destructive/10 hover:text-destructive-emphasis max-sm:h-11'
      >
        {pending ? (
          <Loader2 className='h-3 w-3 animate-spin' aria-hidden />
        ) : (
          <Trash2 className='h-3 w-3' aria-hidden />
        )}
        Remove from mindspace
      </Button>
    </div>
  );
}

/**
 * §2.10 #38 — the user paused this concept.
 *
 * The copy enumerates every surface the pause affects (Practice Arena, Quick
 * Bytes, the default graph) rather than saying "excluded from reviews". A user
 * who paused a concept a week ago and now cannot find it in Quick Bytes has no
 * other way to connect the two.
 */
export function PausedBanner({
  onResume,
  pending,
}: {
  onResume: () => void;
  pending: boolean;
}) {
  return (
    <div className='space-y-2 rounded-lg border border-border bg-muted/50 p-3'>
      <div className='flex items-start gap-2'>
        <PauseCircle
          className='mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground'
          aria-hidden
        />
        <div>
          <p className='text-sm font-medium text-foreground'>
            Paused from reviews
          </p>
          <p className='mt-0.5 text-xs text-muted-foreground'>
            Excluded from Practice Arena and Quick Bytes, and hidden from the
            default graph. Review history is preserved.
          </p>
        </div>
      </div>
      <Button
        variant='outline'
        size='sm'
        onClick={onResume}
        disabled={pending}
        className='w-full gap-1.5 max-sm:h-11'
      >
        {pending ? (
          <Loader2 className='h-3 w-3 animate-spin' aria-hidden />
        ) : (
          <PlayCircle className='h-3 w-3' aria-hidden />
        )}
        Resume reviews
      </Button>
    </div>
  );
}
